/**
 * Intent engine types: campaign intents, suggested actions, and insights baselines.
 * Versioned, extensible; intents can come from many sources (rules, LLM, etc.).
 */

/** Schema version for the intent payload; bump when we change shape or semantics. */
export type IntentSchemaVersion = "1";

/** Why this intent was produced: short summary and optional structured steps. */
export interface IntentReasoning {
  summary: string;
  /** One-line justification for UI (e.g. "ROAS below target"). Prefer this for card display. */
  short_summary?: string;
  steps?: string[];
  details?: Record<string, unknown>;
}

/** Context under which the intent was evaluated (rules version, criteria, data ref). */
export interface IntentEvaluationContext {
  /** Version of the rule set used (e.g. "pause_underperformers_v1"). */
  rule_set_version: string;
  /** Human-readable name of the rule set. */
  rule_set_name?: string;
  /** ISO timestamp when the evaluation ran. */
  evaluated_at: string;
  /** Criteria actually used (thresholds, flags) so we can interpret later. */
  criteria_used?: Record<string, unknown>;
  /** Optional ref to the data snapshot (e.g. metrics job id) for audit. */
  data_ref?: string;
}

/**
 * Why this intent was produced: audit + human narrative. Stored once per intent (replaces context + reasoning).
 * Merge InsightEvaluationContext + InsightReasoning when writing; readers use this single block.
 */
export interface IntentExplanation {
  evaluated_at: string;
  rule_set_version?: string;
  rule_set_name?: string;
  criteria_used?: Record<string, unknown>;
  data_ref?: string;
  summary: string;
  /** One-line justification for UI (e.g. "ROAS below target"). Shown before action label. */
  short_summary?: string;
  steps?: string[];
  details?: Record<string, unknown>;
}

/** Intent severity for prioritization and UI. */
export type IntentSeverity = "info" | "warning" | "error";

/**
 * External entity that contributed to this intent (platform-agnostic).
 * References the ad platform's IDs (campaign, ad set, ad, creative, etc.)
 * so we can trace which entities led to the decision without assuming one platform.
 */
export interface IntentSourceEntity {
  /** Entity kind: campaign, adset, ad, ad_creative, audience, etc. */
  type: "campaign" | "adset" | "ad" | "ad_creative" | "audience" | string;
  /** ID in the ad platform (e.g. Meta ad set id, TikTok campaign id). */
  external_id: string;
  /** Ad platform identifier when relevant (e.g. "META", "TIKTOK"). Not required. */
  platform?: string;
  /** Entity id (e.g. campaign id, adset id) for lookup. */
  entity_id?: string;
}

/**
 * Common properties for every suggested action (entity target).
 * Each item in suggested_actions has a type plus these fields and type-specific fields.
 */
export interface SuggestedActionCommon {
  entity_type: IntentEntityType;
  entity_id: string;
  external_id?: string;
  /** Concrete one-liner for UI (e.g. "Toggle off ad set \"Summer Sale\"", "Increase budget of \"Summer Sale\" by 20%"). */
  label?: string;
}

/** Canonical action types: minimal set of executable operations. entity_type indicates target (adset, campaign, etc.). */
export type CanonicalActionType =
  | "PAUSE"
  | "INCREASE_BUDGET"
  | "REFRESH_CREATIVE";

export interface SuggestedActionPauseAdset extends SuggestedActionCommon {
  type: "PAUSE";
  reason?: string;
}

export interface SuggestedActionPauseAd extends SuggestedActionCommon {
  type: "PAUSE";
  reason?: string;
}

export interface SuggestedActionIncreaseBudget extends SuggestedActionCommon {
  type: "INCREASE_BUDGET";
  reason?: string;
  step_pct?: number;
}

/** Informational: suggest refreshing creative for an ad (e.g. creative fatigue). Not executable; entity_type/entity_id identify the target. */
export interface SuggestedActionRefreshCreative extends SuggestedActionCommon {
  type: "REFRESH_CREATIVE";
  reason?: string;
}

/**
 * One suggested action. Flat array: one item per action; executors iterate and switch on type.
 * action.type is canonical (PAUSE | INCREASE_BUDGET | REFRESH_CREATIVE); intent_type on the parent intent = remedy category (e.g. PAUSE_ADSET).
 */
export type SuggestedActionItem =
  | SuggestedActionPauseAdset
  | SuggestedActionPauseAd
  | SuggestedActionIncreaseBudget
  | SuggestedActionRefreshCreative;

/**
 * Serializable action: can be stored and executed later when the user acts on an intent.
 * type = canonical operation (PAUSE | INCREASE_BUDGET); intent_type on the parent intent = remedy category.
 */
export type Action = SuggestedActionItem;

/**
 * Intent type = remedy category (what we recommend), not the diagnosis.
 * action.type = canonical operation (PAUSE | INCREASE_BUDGET). For single-action intents they often align (e.g. intent_type PAUSE_ADSET → action type PAUSE).
 * Add a new value here when introducing a new kind of intent.
 */
export type IntentType =
  | "PAUSE_ADSET"
  | "PAUSE_AD"
  | "PAUSE_CAMPAIGN"
  | "INCREASE_BUDGET"
  | "CONSOLIDATE_ADSETS"
  | "INFORM_CREATIVE_FATIGUE"
  | "TOO_EARLY_TO_TELL";

/** All intent types in order. Single source of truth for ads-campaign-insights and config APIs. */
export const INTENT_TYPES: IntentType[] = [
  "PAUSE_ADSET",
  "PAUSE_AD",
  "PAUSE_CAMPAIGN",
  "INCREASE_BUDGET",
  "CONSOLIDATE_ADSETS",
  "INFORM_CREATIVE_FATIGUE",
  "TOO_EARLY_TO_TELL",
];

/** Entity types an intent can target. Same as SuggestedActionCommon.entity_type. Single source for findings, actions, and schema. */
export type IntentEntityType = "campaign" | "adset" | "ad" | "ad_creative";

/** All entity types (runtime list for schema/enums). Use this instead of repeating the union. */
export const ENTITY_TYPES: readonly IntentEntityType[] = [
  "campaign",
  "adset",
  "ad",
  "ad_creative",
];

/** Allowed entity types per intent type. Used by ads-campaign-insights and config. */
export const INTENT_TYPE_ALLOWED_ENTITY_TYPES: Record<
  IntentType,
  readonly IntentEntityType[]
> = {
  PAUSE_ADSET: ["adset"],
  PAUSE_AD: ["ad"],
  PAUSE_CAMPAIGN: ["campaign"],
  INCREASE_BUDGET: ["campaign"],
  CONSOLIDATE_ADSETS: ["campaign"],
  INFORM_CREATIVE_FATIGUE: ["ad"],
  TOO_EARLY_TO_TELL: ["adset"],
};

/** Human-readable label per intent type (for UI). */
export const INTENT_TYPE_LABELS: Record<IntentType, string> = {
  PAUSE_ADSET: "Pause ad set",
  PAUSE_AD: "Pause ad",
  PAUSE_CAMPAIGN: "Pause campaign",
  INCREASE_BUDGET: "Increase budget",
  CONSOLIDATE_ADSETS: "Consolidate ad sets",
  INFORM_CREATIVE_FATIGUE: "Creative fatigue",
  TOO_EARLY_TO_TELL: "Too early to tell",
};

export type CampaignIntentStatus =
  | "SUGGESTED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "FAILED"
  | "DISABLED" // superseded by a newer run; do not show to user
  | "IGNORED"; // user dismissed; do not show again

/**
 * Entity target for an intent (campaign, adset, ad, creative).
 * Use for display, filtering, deduplication. Agnostic to entity type.
 */
export interface IntentTargetEntity {
  entity_type: "campaign" | "adset" | "ad" | "ad_creative";
  entity_id: string;
}

/**
 * Intent = intent to take action to fix a diagnosis. Composed of serializable actions
 * that will be executed when the user decides to act on this intent.
 * (Stored in DB with suggested_actions = intent.actions.)
 * Prefer explanation (single block); context/reasoning kept for backward compat when reading old records.
 */
export interface CampaignIntent {
  id: string;
  campaign_id: string;
  event_id?: string;
  organization_id?: string;
  intent_type: IntentType;
  actionable: boolean;
  reason?: string;
  /**
   * Entity targets for this intent. Use for display, filtering, deduplication.
   * Derived from actions when not set. Replaces adset_ids for new intents.
   */
  target_entities?: IntentTargetEntity[];
  created_at?: string;
  /** info | warning | error; used for prioritization and UI. */
  severity?: IntentSeverity;
  /** Serializable actions to execute when the user acts on this intent. */
  actions: Action[];
  /** Why this intent was produced (audit + narrative). Replaces context + reasoning for new intents. */
  explanation?: IntentExplanation;
  source_entities?: IntentSourceEntity[];
  /** Ids of the finding(s) that led to this intent (diagnosis → remedy link). */
  finding_ids?: string[];
  /** Campaign kind (e.g. "ads", "email") for filtering and future multi-kind intents. */
  campaign_kind?: string;
  /** Who produced this intent: rule = rule-based diagnostician (user-facing); llm = LLM (dev/debug). Set at write time. */
  source?: "rule" | "llm";
  /** Status when stored (SUGGESTED = applicable; IN_PROGRESS/COMPLETED/FAILED = not applicable). */
  status?: CampaignIntentStatus;
  /** When the intent was applied (set when status becomes COMPLETED). ISO 8601. */
  applied_at?: string;
  /** Last persistence time for this intent record (e.g. status transitions). ISO 8601. */
  updated_at?: string;
}

/** Intent the user can apply; apply success toasts and copy helpers use this only. */
export type ActionableCampaignIntent = CampaignIntent & { actionable: true };

export function isActionableIntent(
  intent: CampaignIntent
): intent is ActionableCampaignIntent {
  return intent.actionable === true;
}

function firstIntentAction(
  intent: ActionableCampaignIntent
): SuggestedActionItem | undefined {
  return intent.actions?.[0];
}

/** Short toast title after an actionable intent was applied (what changed). */
export function getIntentApplyOutcomeTitle(
  intent: ActionableCampaignIntent
): string {
  const t = intent.intent_type;
  if (t === "PAUSE_ADSET") return "Ad set paused";
  if (t === "PAUSE_AD") return "Ad paused";
  if (t === "PAUSE_CAMPAIGN") return "Campaign paused";
  if (t === "INCREASE_BUDGET") return "Budget increased";
  if (t === "CONSOLIDATE_ADSETS") return "Ad sets consolidated";
  if (t === "INFORM_CREATIVE_FATIGUE") return "Creative insight saved";
  if (t === "TOO_EARLY_TO_TELL") return "Change applied";
  const a = firstIntentAction(intent);
  if (a?.type === "PAUSE") {
    if (a.entity_type === "ad") return "Ad paused";
    if (a.entity_type === "adset") return "Ad set paused";
    if (a.entity_type === "campaign") return "Campaign paused";
    return "Paused";
  }
  if (a?.type === "INCREASE_BUDGET") return "Budget increased";
  return "Change applied";
}

/**
 * One-line explanation of what was applied (past tense). Shown under the title in toasts
 * or combined with partial API warnings.
 */
export function getIntentApplyOutcomeDescription(
  intent: ActionableCampaignIntent
): string {
  const t = intent.intent_type;
  if (t === "PAUSE_ADSET") return "The ad set was paused.";
  if (t === "PAUSE_AD") return "The ad was paused.";
  if (t === "PAUSE_CAMPAIGN") return "The campaign was paused.";
  if (t === "INCREASE_BUDGET") return "The campaign budget was increased.";
  if (t === "CONSOLIDATE_ADSETS")
    return "The recommended ad set consolidation was applied.";
  if (t === "INFORM_CREATIVE_FATIGUE")
    return "The creative fatigue recommendation was saved.";
  if (t === "TOO_EARLY_TO_TELL") return "The recommended change was applied.";
  const a = firstIntentAction(intent);
  if (a?.type === "PAUSE") {
    if (a.entity_type === "ad") return "The ad was paused.";
    if (a.entity_type === "adset") return "The ad set was paused.";
    if (a.entity_type === "campaign") return "The campaign was paused.";
    return "The target was paused.";
  }
  if (a?.type === "INCREASE_BUDGET") return "The budget was increased.";
  return "The recommended action was applied.";
}

/**
 * Success toast after batch-apply when we are not using guided `pollPublishAfterIntentApply`.
 * Call only after `pollBatchIntentApplyUpdates` returns: either there was no `update_id`
 * or publish polling finished — never imply an in-flight sync.
 */
export function buildIntentApplyImmediateToastCopy(
  intent: ActionableCampaignIntent,
  partial: string | null | undefined
): { title: string; description: string } {
  const title = getIntentApplyOutcomeTitle(intent);
  const base = getIntentApplyOutcomeDescription(intent);
  const partialText =
    partial != null && String(partial).trim().length > 0
      ? String(partial).trim()
      : null;
  const description =
    partialText != null ? `${base} Some issues: ${partialText}` : base;
  return { title, description };
}

/**
 * Organization-level baselines for ads campaign (diagnosticians and action generators).
 * Stored in marketing table: pk = ORGANIZATION#${organization_id}, sk = !#ADS_INSIGHTS_BASELINES.
 * All fields optional; code defaults apply when not set.
 */
export interface OrganizationAdsIntentsBaselines {
  /**
   * Target ROAS (return on ad spend) threshold for underperformer diagnosis.
   * If an ad set's ROAS is below this value (and learning is over and min_spend met), it is diagnosed as underperforming and the pause-underperformers action may suggest pausing it. When unset, underperformer diagnostician does not run.
   */
  roas_below?: number;

  /**
   * Target ROAS threshold for increase-budget candidate diagnosis.
   * Campaign spend-weighted ROAS at or above this value (with min_spend and checkout gates met) yields an increase-budget candidate. When unset, that diagnostician does not run.
   */
  roas_at_least?: number;

  /**
   * Minimum spend (in organization currency units) required before an ad set is considered for pause or increase-budget rules. Ad sets with spend below this are skipped by underperformer and increase-budget diagnosticians. Default: 0.
   */
  min_spend?: number;

  /**
   * Frequency (impressions per user) above which creative fatigue is diagnosed. Ad sets with frequency at or above this get a creative-fatigue finding (informational: suggest adding new creative). Default: 4.
   */
  frequency_above?: number;

  /**
   * Legacy baseline flag. Underperformer diagnosis ignores Meta learning phase; kept for stored org/platform records. High-performing tiers use checkout counts, not learning. Default: false.
   */
  skip_if_in_learning?: boolean;

  /**
   * How to treat an ad set whose Meta learning_phase is missing/unknown for the still-learning diagnostician only. "exited" = behave as if learning finished (code default). "skip" = do not treat as exited (conservative). Underperformer and high-performing rules ignore this. Default: "exited".
   */
  unknown_learning_treat_as?: "exited" | "skip";

  /**
   * Ad set daily budget (currency units) below which "increase_budget" is suggested when the ad set is in Learning limited. Used by the learning-limited diagnostician to decide suggested options. Default: 100.
   */
  learning_limited_budget_threshold?: number;

  /**
   * Suggested percentage step when increasing ad set budget (e.g. 20 = increase by 20%). Used by increase-budget action generator and learning-limited generator for intent labels and reasoning. Default: 20.
   */
  increase_budget_step_pct?: number;

  /**
   * Minimum whole days after a user-applied (COMPLETED) or in-flight (IN_PROGRESS) INCREASE_BUDGET intent before the pipeline may suggest increasing budget again for the same campaign. Aligns with "wait several days between steps" guidance. Default: 4. Set to 0 to disable cooldown.
   */
  increase_budget_cooldown_days?: number;

  /**
   * Minimum checkout conversions (purchase-style signal: ad set metrics `conversions` / `platform_purchase_count` / Meta purchase actions).
   * Used everywhere we gate on “enough conversion data”: (1) **campaign total** (sum across ACTIVE ad sets with `external_id`) before campaign-level increase-budget intents (`increase_budget_candidate`, learning-limited INCREASE_BUDGET path) and before `adset_underperforming` / `ad_underperforming` findings; (2) **each ad set’s own** count before `adset_high_performing` and before ads qualify as `ad_high_performing` under that parent.
   * Set to 0 to disable. Default: 10.
   */
  min_checkouts?: number;

  /**
   * Minimum impressions required before an ad set can be diagnosed as underperforming. Ensures we only judge ad sets that had actual delivery. Use 0 to require only impressions > 0. Default: 0.
   */
  min_impressions?: number;

  /**
   * Minimum days the ad set must have been active (since start_time or created_at) before it can be diagnosed as underperforming. 0 = no time check. Default: 0.
   */
  min_days_active?: number;

  /**
   * Organization baseline CPA (cost per acquisition) in currency units. When set, the learning-limited diagnostician checks if baseline_cpa < budget/50 — if so, budget is sufficient to achieve 50 conversions at baseline CPA, and the diagnostic flags this (suggesting consolidation or audience broadening rather than budget increase).
   */
  cpa_baseline?: number;

  /**
   * Organization baseline CPM (cost per 1000 impressions) in currency units. When set, the learning-limited diagnostician checks if baseline_cpm < budget / (impressions_needed_for_50_conv / 1000) — if so, budget is sufficient at baseline CPM. Requires conversion rate from adset metrics.
   */
  cpm_baseline?: number;

  /**
   * Default days in learning beyond which an ad set is considered "stuck" if conversions are still very low (used when Meta does not provide dynamic_lp_days_threshold). Learning-limited generator suggests PAUSE instead of INCREASE_BUDGET when stuck. Default: 14.
   */
  learning_stuck_days_default?: number;

  /**
   * Minimum conversions below which an ad set in learning is considered not making progress when past the days threshold. Used together with learning_stuck_days_default / dynamic_lp_days_threshold. Default: 10.
   */
  learning_stuck_min_conversions?: number;

  /**
   * Minimum fraction of Meta's dynamic_lp_conversions_threshold below which we consider the ad set stuck (when that threshold is set). E.g. 0.25 = stuck if conversions < 25% of threshold. Default: 0.25.
   */
  learning_stuck_conversions_fraction?: number;
}

/** Stored metadata for one intent record (pk CAMPAIGN#id, sk !#CAMPAIGN_INTENT#intent_id). Used when intents are stored as separate records. */
export type CampaignIntentItemMetadata = CampaignIntent & {
  /** "rule" = user-facing; "llm" = dev-only, excluded from API. */
  source?: "rule" | "llm";
  /** Same as CampaignIntentStatus. SUGGESTED = current; DISABLED = superseded by a newer run (do not show). */
  status?: CampaignIntentStatus;
  schema_version?: IntentSchemaVersion;
};
